import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

/**
 * Fullscreen zoom lightbox for hero / product images.
 *
 * Interactions:
 *  • Click backdrop or press ESC      → close
 *  • Mouse wheel                      → zoom in/out (1×–5×)
 *  • Click-drag (while zoomed)        → pan
 *  • Double click                     → toggle 1×/2.5×
 *  • Pinch gesture (touch)            → native (CSS touch-action)
 *  • Toolbar buttons                  → ± zoom, reset
 *
 * Designed to feel like an industrial product catalog viewer: silent,
 * fast, no decorative chrome. The image stays sharp because the same
 * URL is requested at full resolution (the catalog hero uses the same
 * source); the browser caches it.
 */
const MIN_SCALE = 1;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.4;

export default function ZoomLightbox({ open, src, alt, onClose }) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const dragRef = useRef(null);

  const reset = useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, []);

  // Reset transform whenever a new image opens.
  useEffect(() => {
    if (open) reset();
  }, [open, src, reset]);

  // Keyboard shortcuts. Bound to window so they fire from anywhere
  // (otherwise focus would need to be inside the modal).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "+" || e.key === "=") setScale((s) => Math.min(MAX_SCALE, s + ZOOM_STEP));
      else if (e.key === "-" || e.key === "_") setScale((s) => Math.max(MIN_SCALE, s - ZOOM_STEP));
      else if (e.key === "0") reset();
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, reset]);

  if (!open || !src) return null;

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    setScale((s) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s + delta)));
  };

  const onPointerDown = (e) => {
    if (scale <= 1) return; // panning only makes sense while zoomed
    dragRef.current = { startX: e.clientX, startY: e.clientY, tx, ty };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setTx(dragRef.current.tx + dx);
    setTy(dragRef.current.ty + dy);
  };
  const onPointerUp = () => { dragRef.current = null; };

  const onDoubleClick = () => {
    setScale((s) => (s > 1 ? 1 : 2.5));
    setTx(0); setTy(0);
  };

  // Stop clicks on the image area from bubbling to the backdrop (which
  // would close the modal). Toolbar uses stopPropagation per button.
  const stop = (e) => e.stopPropagation();

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/85 backdrop-blur-sm animate-in fade-in duration-150"
      data-testid="zoom-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={alt || "Productfoto zoom"}
      onClick={onClose}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3" onClick={stop}>
        <div className="font-mono text-[11px] uppercase tracking-widest text-white/70">
          {scale.toFixed(1)}× zoom · scroll · sleep · dubbelklik
        </div>
        <div className="flex items-center gap-1.5">
          <ToolbarButton onClick={() => setScale((s) => Math.max(MIN_SCALE, s - ZOOM_STEP))} testId="zoom-out" title="Uitzoomen (−)">
            <ZoomOut size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={() => setScale((s) => Math.min(MAX_SCALE, s + ZOOM_STEP))} testId="zoom-in" title="Inzoomen (+)">
            <ZoomIn size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={reset} testId="zoom-reset" title="Reset (0)">
            <RotateCcw size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={onClose} testId="zoom-close" title="Sluiten (Esc)">
            <X size={16} />
          </ToolbarButton>
        </div>
      </div>

      {/* Image stage */}
      <div
        className="flex flex-1 items-center justify-center overflow-hidden select-none"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
        onClick={stop}
        style={{
          cursor: scale > 1 ? (dragRef.current ? "grabbing" : "grab") : "zoom-in",
          touchAction: "pinch-zoom",
        }}
      >
        <img
          src={src}
          alt={alt || ""}
          draggable={false}
          data-testid="zoom-lightbox-image"
          className="max-h-full max-w-full object-contain transition-transform duration-100 ease-out will-change-transform"
          style={{
            transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
            transformOrigin: "center center",
          }}
        />
      </div>

      <div className="border-t border-white/10 px-4 py-2 text-center font-mono text-[10px] uppercase tracking-widest text-white/40" onClick={stop}>
        Klik buiten de foto of druk op Esc om te sluiten
      </div>
    </div>,
    document.body
  );
}

function ToolbarButton({ children, onClick, title, testId }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      title={title}
      aria-label={title}
      data-testid={testId}
      className="inline-flex h-8 w-8 items-center justify-center rounded border border-white/15 bg-white/5 text-white/80 transition hover:bg-white/15 hover:text-white"
    >
      {children}
    </button>
  );
}
